/**
  * @class meleCalendar
  * @description
  *
  * # Purpose
  * Implements `<mele-calendar>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-calendar></mele-calendar>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-calendar',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleView from 'meleView'
const moment = require('moment')

const PROPERTIES = _.extend({}, {
  title: { type: 'string', description: 'title of the calendar' },
  type: { type: 'string', description: 'type of calendar', enum: ['datetime', 'date', 'time'], defualt: 'datetime' },
  tab: { type: 'string', description: 'Active view of the time. options', enum: ['year', 'month', 'day', 'hour', 'minute', 'second'] },
  time: { type: 'number', description: 'Unix timestamp of selected time' },
  jump: { type: 'boolean', description: 'Jumps to next tab', default: true },
  nosecond: { type: 'boolean', description: 'Disables seconds' },
  selected: { type: 'string', description: 'Selected item' },
  rounding: { type: 'string', description: 'rounding of time', enum: ['start', 'now', 'end'], defualt: 'now' },
  eventCalendarChanged: { type: 'function', description: 'called when calendar content change' },
  eventCalendarDone: { type: 'function', description: 'called when last input selected' },
}, meleView._properties)

export default class meleCalendar extends meleView {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()
    this._title = this._header_wrapper.appendChildren({ tag: 'mele-text', class: 'calendar-title' })
    this._datetime = this._header_wrapper.appendChildren({ tag: 'mele-timestamp' })
    // this._tabname =  this._header_wrapper.appendChildren({tag: 'mele-text', class:"calendar-title"});
    this._datetime.eventSelected = (el, selected)=>{
      if(this.tab!=selected) {
        this.tab=selected
      }
    }
    this._datetime._body.forAll('mele-sinput', el=>{
      el.eventValueChanged = (el, value)=>{
        if(!el.invalid) {
          if(el.key != this.tab) return
          value = el.key == 'month' ? parseInt(value)-1 : parseInt(value)
          if(value == this.selected) return
          this.selected = value
        }
      }
      el.eventEnterPressed = ()=>{
        if(!el.invalid) {
          const elModal = this.closest('.modal-calendar')
          if(elModal) elModal.formSave()
        }
      }
    })
  } // init

  titleChanged(value) {
    if(value) {
      this._title.label = value
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "time"
  */
  timeChanged(value, old) {
    const old_time = this.getTime(old)
    const new_time = this.getTime(value)

    if(this._datetime.time != value) {
      this._datetime.time = value
    }

    const prevTab = this.prevTab(this.tab)
    if(prevTab) {
      old_time.startOf(prevTab)
      new_time.startOf(prevTab)
    }

    const should_refresh = old_time.valueOf() != new_time.valueOf()

    if(should_refresh) {
      this.refreshContent()
    }

    if(typeof this.eventCalendarChanged==='function') {
      this.eventCalendarDone(this)
    }
  } // timeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "tab"
  */
  tabChanged(value, old, initial) {
    if(value) {
    // this._tabname.label = value;
      this._datetime.selected = value
      if(!initial) {
        this.refreshContent()
      }
    }
  } // tabChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "jump"
  */
  jumpChanged(value) {

  } // jumpChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "selected"
  */
  selectedChanged(value, old, context) {
    if(context == 'rerender') {
      this._changeMonth(value)
      return
    }
    this.toggleSelected(value, old)
    if(context == 'render') return
    // get moment object
    let selected_time = this.getTime(this.time)
    // change part of the time based on tab name(day, month, etc) with selected value
    if(this.tab && typeof selected_time[this.tab] === 'function') {
      if(this.tab== 'day') {
        selected_time.date(value)
      } else{
        selected_time[this.tab](value)
      }
    }

    if(/year|month|day/.test(this.tab)) {
      selected_time = this.coerceTime(selected_time)
    }

    this.time = moment(selected_time).unix()
  } // selectedChanged

  coerceTime(time) {
    if(this.rounding == 'end') {
      return moment(time).endOf('day')
    } else if(this.rounding == 'start') {
      return moment(time).startOf('day')
    } else {
      return time
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "selected"
  */
  typeChanged(value) {
    if(value && this._datetime.type != value) {
      this._datetime.type = value
    }
  } // typeChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "rounding"
  */
  roundingChanged(value) {
  } // roundingChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * refresh content of calendar
  */
  refreshContent() {
    const tab = this.tab
    const items = []

    const dt = this.getTime(this.time)
    let selected
    if(tab=='year') {
      selected = dt.year()
      _.range(parseInt(dt.year())-4, parseInt(dt.year())+5, 1).forEach(i=>
        items.push({ key: i, label: i, class: 'calendar year' }),
      )
    }else if(tab=='month') {
      selected = dt.month()
      for(let m=0; m<=11; m++) { items.push({ key: m, label: moment.months()[m], class: 'calendar month' }) }
    }else if(tab=='day') {
      selected = dt.date()
      const day = moment(dt.clone().startOf('month').isoWeekday(1))

      for(let d = 0; d < 42; d++) {
        var key
        if (dt.month() == day.month()) {
          key = day.date()
        } else if(dt.month() < day.month()) {
          key = 'n'+day.date()
        } else if(dt.month() > day.month()) {
          key = 'p'+day.date()
        }
        items.push({ key, label: _.two(day.date()), class: 'calendar day', disabled: dt.month() != day.month() })
        day.add(1, 'day')
      }
      // hack to remove disabled weeks from calendar
      if(items[6].disabled) {
        items.splice(0, 6)
      } else if(items[28].disabled) {
        items.splice(28, 41)
      } else if(items[35].disabled) {
        items.splice(35, 41)
      }
    }else if(tab=='hour') {
      selected = dt.hour()
      for(let h=0; h<=23; h++) {
        const cls = h<12 ? 'calendar hour am' : 'calendar hour pm'
        items.push({ key: h, label: _.two(h), class: cls })
      }
    }else if(tab=='minute') {
      selected = dt.minute()
      for(let t=0; t<=59; t++) { items.push({ key: t, label: _.two(t), class: 'calendar minute' }) }
    }else if(tab=='second') {
      selected = dt.second()
      for(let s=0; s<=59; s++) { items.push({ key: s, label: _.two(s), class: 'calendar second' }) }
    }
    this.setItems(items)

    this.setProperty('selected', selected, 'render')

    if(this._datetime.time != this.time) {
      this._datetime.time = this.time
    }

    if(typeof this.eventCalendarChanged==='function') {
      this.eventCalendarChanged(this, this.time, this.tab)
    }
  } // refreshContent

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * switches currently selected item
  * @param {string} key
  * @param {string} old_key
  */
  toggleSelected(key, old_key) {
    let el
    if(key) {
      el = this.getItemElement(key)
      if(!el || el.selected) return
      el.setAttribute('selected', '')
    }
    if(old_key) {
      el = this.getItemElement(old_key)
      if(!el) return
      el.removeAttribute('selected')
    }
  } // toggleSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * returns moment time object from unix timestamp
  * @param {integer} timestamp
  */
  getTime(timestamp) {
    return moment(parseInt(timestamp)).isValid() ? moment(moment.unix(timestamp).format()) : moment()
  } // getTime

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * returns previous tab
  */
  prevTab(tab) {
    const tabs = _.compact(this._datetime.items.map(item=>item.key))
    const current = tabs.indexOf(tab)

    if(current > 0) {
      return tabs[current-1]
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * returns next tab
  */
  nextTab() {
    const tabs = _.compact(this._datetime.items.map(item=>item.key))
    if(this.type=='date') tabs.length=3
    else if(this.type=='time') tabs.splice(0, 3)
    const current = tabs.indexOf(this.tab)
    let next = current<tabs.length-1 ? tabs[current+1] : tabs[current]
    if(this.nosecond && next=='second') next = tabs[current]
    return next
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * changes item used in setItem to mele-text
  */
  coerceItem(json) {
    const _this = this
    if(_.isObjectLike(json)) {
      json = _.extend({
        tag: 'mele-text',
        label: _.toString(this.label),
        onclick: (e)=>{
        // _this.selected = e.target.key
          const context = /(p)|(n)/.test(e.target.key) ? 'rerender' : 'clicked'
          _this.setProperty('selected', e.target.key, context, this.jump)
          if(this.jump && context === 'clicked') {
            this.tab = this.nextTab()
          }
        },
      }, json)
      return json
    }else{
      return null
    }
  } // coerceItem

  _changeMonth(key) {
    const m = key.match(/([p,n])(\d+)/)
    if(!m) return
    const time = this.getTime(this.time)
    if(m[1] == 'n') {
      time.add(1, 'month')
    } else if (m[1] == 'p') {
      time.subtract(1, 'month')
    }
    this.time = time.unix()
    this.setProperty('selected', m[2], 'render')
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle event "CalendarChanged"
  */
  eventCalendarChanged(el, time, tab) {
    this.publish('mmCalendarChanged', {
      holder: el.key,
      tab,
      time,
    })
  } // eventCalendarChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle event "CalendarDone"
  */
  eventCalendarDone(self) {
    this.publish('mmCalendarDone', {
      holder: this.key,
    })
  } // eventCalendarDone
} // class meleCalendar

meleCalendar._properties = PROPERTIES
meleCalendar.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-calendar', meleCalendar)
